import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, IconButton, Paper, Typography } from "@mui/material";
import { Download, Send } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import CustomDatePicker from "../../common_components/daterangepicker/CustomDatePicker";
import { Dropdown } from "../../common_components/FormInputs";
import AppButton from "../../common_components/AppButton";
import ReportActionDialog, { DialogNote } from "./components/ReportActionDialog";
import CommonLoader from "../../common_components/CommonLoader";
import { toast } from "../../common_components/Toast/ToastUtils";
import { setActiveModuleKey } from "../../redux/userDataSlice";
import { selectCanManageReports, selectCanManageAutoSchedule, selectCanManageNotes, selectEntityType, selectIsSuperAdmin, selectToken, selectUser, } from "../../redux/authSlice";
import { linkDraftToReport } from "./askAiConversation";
import reportBuilderService from "../../services/reportBuilder.service";
import { payloadToBuilderState, toApiColumnKey } from "./reportPayload";
import { ROUTES } from "../../routes/routes.constants";
import { useClientSelection } from "../../hooks";
import { clearCache } from "../../helper/cache";
import { DEFAULT_REPORT_NAME, DIMENSION_LABELS, createDefaultBuilderState } from "./reportBuilder.utils";
import { calendarDateToIso, fmtDateRange, isoToCalendarDate } from "./createReport.utils";
import ReportBuilder from "./components/ReportBuilder";
import { exportReportExcel } from "./exportReportExcel";
import CloneReportModal, { useCloneReport } from "./components/CloneReportModal";
import ReportNameEditor from "./components/ReportNameEditor";
import AskAiButton from "./components/AskAiButton";
import ReportNotesDrawer from "./components/ReportNotesDrawer";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
const TITLES = {
    add: "Add Report",
    edit: "Edit Report",
    view: "View Report",
};
const MODULE_KEY = "create_report";
/** The page's record for a report, from its builder selection. */
const toReportRow = (builderState, reportId, clientId, clientName, createdAt) => ({
    report_id: reportId,
    report_name: builderState.report_name,
    client_id: clientId,
    client_name: clientName,
    template_id: builderState.template_id,
    template_name: "",
    created_by_name: "",
    created_by_email: "",
    created_at: createdAt,
    period_start: builderState.period_start,
    period_end: builderState.period_end,
    compare_start: builderState.compare_start,
    compare_end: builderState.compare_end,
    status: "ready",
    recipients: [],
    schedule: null,
    last_sent_at: null,
    builder_state: builderState,
});
const CreateReportForm = ({ mode }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { reportId } = useParams();
    const isView = mode === "view";
    const isAdd = mode === "add";
    const isEdit = mode === "edit";
    const hasManageReports = useSelector(selectCanManageReports);
    const hasManageAutoSchedule = useSelector(selectCanManageAutoSchedule);
    const hasManageNotes = useSelector(selectCanManageNotes);
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    // Client contacts are view-only - no add / edit, no client picker, no export.
    const isClient = useSelector(selectEntityType) === "client";
    const canManage = !isClient && (isSuperAdmin || hasManageReports);
    const canManageAutoSchedule = !isClient && (isSuperAdmin || hasManageAutoSchedule);
    const canManageNotes = !isClient && (isSuperAdmin || hasManageNotes);
    const token = useSelector(selectToken) || "";
    const authUser = useSelector(selectUser);
    // Client scope is global (shared with the listing through useClientSelection),
    // so a report added here belongs to whichever client the listing had active.
    // Edit / view do not use it: they show the client the report was made for.
    const { selectedId, selectedClientId, selectedGroupAccountId, clientOptions, groupAccountOptions, utilityLoading, handleClientChange, handleGroupChange, } = useClientSelection(MODULE_KEY);
    const selectedClientName = clientOptions.find((opt) => opt.value === selectedId)?.label || "";
    const [report, setReport] = useState(null);
    /** Report data loaded with the saved report (edit / view); null on add. */
    const [reportData, setReportData] = useState(null);
    /** Shown above the report when some of its networks have no account for this client. */
    const [networkWarning, setNetworkWarning] = useState("");
    const [loading, setLoading] = useState(!isAdd);
    const [notFound, setNotFound] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    /**
     * Latest builder selection. Held in a ref so the builder's every keystroke
     * does not re-render this page; the pieces the header actually needs are
     * mirrored into `saveState` below.
     */
    const snapshotRef = useRef(null);
    const [builderMeta, setBuilderMeta] = useState({
        hasGenerated: false,
        isValid: false,
        generatedAt: null,
        refreshing: false,
        hasReport: false,
        // Picked dimensions, joined — feeds the automatic name on Add.
        dimensionKey: "",
    });
    const handleSnapshot = useCallback((snapshot) => {
        snapshotRef.current = snapshot;
        setBuilderMeta((prev) => prev.hasGenerated === snapshot.hasGenerated &&
            prev.isValid === snapshot.isValid &&
            prev.generatedAt === snapshot.generatedAt &&
            prev.refreshing === snapshot.refreshing &&
            prev.hasReport === snapshot.hasReport &&
            prev.dimensionKey === snapshot.state.dimension_ids.join(",")
            ? prev
            : {
                hasGenerated: snapshot.hasGenerated,
                isValid: snapshot.isValid,
                generatedAt: snapshot.generatedAt,
                refreshing: snapshot.refreshing,
                hasReport: snapshot.hasReport,
                dimensionKey: snapshot.state.dimension_ids.join(","),
            });
    }, []);
    /**
     * Date range. Owned HERE, not in the builder, because the picker sits in the
     * page header — the builder receives the resolved ISO dates as props.
     * Seeded from the builder's own defaults so both agree on first render.
     */
    const [dateRange, setDateRange] = useState(() => {
        const defaults = createDefaultBuilderState();
        return {
            start: isoToCalendarDate(defaults.period_start),
            end: isoToCalendarDate(defaults.period_end),
        };
    });
    const [compareRange, setCompareRange] = useState(null);
    /** Bumped when a date is applied over an existing report: the builder then regenerates it. */
    const [generateToken, setGenerateToken] = useState(0);
    const handleDateChange = useCallback(({ mainRange, compareRange: nextCompare }) => {
        if (mainRange?.start && mainRange?.end)
            setDateRange(mainRange);
        setCompareRange(nextCompare?.start && nextCompare?.end ? nextCompare : null);
        // The picker's button reads "Apply and Generate" once a report exists.
        if (snapshotRef.current?.hasReport)
            setGenerateToken((prev) => prev + 1);
    }, []);
    const [refreshToken, setRefreshToken] = useState(0);
    const refetchReportData = useCallback(async () => {
        setRefreshToken((prev) => prev + 1);
    }, []);
    // Refresh runs in the BACKGROUND (it can take minutes). The report's own
    // refresh_status column says whether one is going, so the page - after a
    // reload, in another tab, for another user - stays blocked until it ends, like
    // Billing Summary's invoice refresh. A finished run is shown once, then cleared.
    /** The start request is in flight. */
    const [refreshStarting, setRefreshStarting] = useState(false);
    /** The report's refresh_status is 'working'. */
    const [refreshRunning, setRefreshRunning] = useState(false);
    /** The finished run's message, in the alert box; hides itself. */
    const [refreshNotice, setRefreshNotice] = useState(null);
    /** A finished run is being shown / cleared - keeps two overlapping checks from showing it twice. */
    const refreshHandlingRef = useRef(false);
    /** Every button that could change the report is blocked meanwhile. */
    const isRefreshing = refreshStarting || refreshRunning;
    const checkRefreshStatus = useCallback(async () => {
        if (!isEdit || isClient || !reportId || refreshHandlingRef.current)
            return;
        try {
            const res = await reportBuilderService.getRefreshStatus(reportId, token);
            const data = res?.data?.data;
            if (!res?.data?.success || !data)
                return;
            if (data.is_syncing) {
                setRefreshRunning(true);
                return;
            }
            setRefreshRunning(false);
            if (!data.status)
                return;
            // Finished: show the outcome once, then remove it from the report.
            refreshHandlingRef.current = true;
            const notice = data.status === "failed"
                ? { severity: "error", text: "Data refresh failed. Please try again." }
                : data.status === "partial"
                    ? {
                        severity: "warning",
                        text: "Data refreshed, but some networks could not be refreshed.",
                    }
                    : { severity: "success", text: "Your data has been refreshed successfully." };
            setRefreshNotice(notice);
            if (notice.severity === "success")
                toast.success(notice.text);
            else if (notice.severity === "warning")
                toast.warning(notice.text);
            else
                toast.error(notice.text);
            if (data.status !== "failed") {
                clearCache();
                refetchReportData();
            }
            try {
                await reportBuilderService.clearRefreshStatus(reportId, token);
            }
            finally {
                refreshHandlingRef.current = false;
            }
        }
        catch {
            // Keep the last known state; the next poll tries again.
        }
    }, [isEdit, isClient, reportId, token, refetchReportData]);
    // On open (also after a reload): is a refresh running, or did one finish unseen?
    useEffect(() => {
        checkRefreshStatus();
    }, [checkRefreshStatus]);
    // While it runs, ask again every 10 seconds until it is over.
    useEffect(() => {
        if (!refreshRunning)
            return;
        const timer = setInterval(checkRefreshStatus, 10000);
        return () => clearInterval(timer);
    }, [refreshRunning, checkRefreshStatus]);
    // The finished-run alert removes itself after a while.
    useEffect(() => {
        if (!refreshNotice)
            return;
        const timer = setTimeout(() => setRefreshNotice(null), 10000);
        return () => clearTimeout(timer);
    }, [refreshNotice]);
    const toIsoDateString = (d) => {
        if (!d)
            return "";
        if (typeof d === "string")
            return d;
        if (d.year && d.month && d.day)
            return calendarDateToIso(d);
        if (typeof d.format === "function")
            return d.format("YYYY-MM-DD");
        if (d instanceof Date)
            return d.toISOString().slice(0, 10);
        return String(d);
    };
    // Refresh Handler — starts the background re-pull of the report's networks
    const handleRefresh = useCallback(async () => {
        if (isRefreshing || !reportId)
            return;
        const clientId = report?.client_id || selectedId;
        const startDate = toIsoDateString(dateRange.start);
        const endDate = toIsoDateString(dateRange.end);
        if (!clientId || !startDate || !endDate)
            return;
        const dateRanges = [{
                startDate,
                endDate,
            }];
        // Only the networks the report's columns come from ("meta.spend" →
        // meta), not every network the client has. No columns yet: all of them.
        const networks = Array.from(new Set((snapshotRef.current?.state.column_order || []).map((id) => toApiColumnKey(id).split(".")[0])));
        setRefreshStarting(true);
        try {
            const res = await reportBuilderService.startRefresh(reportId, { networks, dateRange: dateRanges }, token);
            if (!res?.data?.success) {
                // Already running (another tab or user) - adopt it rather than fail.
                if (/already running/i.test(res?.data?.message || "")) {
                    setRefreshRunning(true);
                    toast.info("Data refresh is in progress. The latest data will be available once the refresh is complete.");
                    return;
                }
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            setRefreshNotice(null);
            setRefreshRunning(true);
            toast.info("Data refresh is in progress. The latest data will be available once the refresh is complete.");
        }
        catch (err) {
            toast.error(err?.message || "Could not start the refresh. Please try again.");
        }
        finally {
            setRefreshStarting(false);
        }
    }, [isRefreshing, reportId, report?.client_id, selectedId, dateRange.start, dateRange.end, token]);
    /**
     * Report name. Owned HERE, like the date range, because it is edited inline
     * in the page header — the builder receives it as a prop and mirrors it into
     * its own state. Hydrated from the loaded record below.
     */
    const [reportName, setReportName] = useState(() => createDefaultBuilderState().report_name);
    /** Right-hand rail visibility. Owned here because the toggle is in the header. */
    // Closed on every load - the report gets the full width, and the user opens
    // the panel by click when needed.
    const [controlsOpen, setControlsOpen] = useState(false);
    /** Ask AI dialog. Owned here because its button is in the header. */
    const [askAiOpen, setAskAiOpen] = useState(false);
    /** The report has already been sent to the client (client_access) - the Send button then reads "Resend Report". */
    const [clientAccess, setClientAccess] = useState(false);
    useEffect(() => {
        dispatch(setActiveModuleKey(MODULE_KEY));
    }, [dispatch]);
    const backToList = () => navigate(ROUTES.createReport.path);
    // Clients only view - Add / Edit URLs send them back to the listing.
    useEffect(() => {
        if (isClient && !isView)
            navigate(ROUTES.createReport.path, { replace: true });
    }, [isClient, isView, navigate]);
    // Someone without "Manage Reports" can't add or edit — send them back.
    useEffect(() => {
        if (!isView && !canManage) {
            toast.error("You do not have permission to manage reports.");
            navigate(ROUTES.createReport.path, { replace: true });
        }
    }, [isView, canManage, navigate]);
    // ── Load the record ─────────────────────────────────────────────────
    // POST /network-report with the report_id alone: the API answers with the
    // stored document as `saved_report`, which refills the builder.
    /** The report id already requested — see the guard below. */
    const loadedIdRef = useRef(null);
    useEffect(() => {
        if (isAdd) {
            loadedIdRef.current = null;
            setReport(null);
            setClientAccess(false);
            setLoading(false);
            setNotFound(false);
            return;
        }
        if (!reportId) {
            setNotFound(true);
            setLoading(false);
            return;
        }
        // One request per report. StrictMode (dev) runs effects twice, and the
        // token can refresh mid-session: the ref keeps both from re-sending it.
        // A reply for a report we have since navigated away from is dropped.
        if (loadedIdRef.current === reportId)
            return;
        loadedIdRef.current = reportId;
        const isCurrent = () => loadedIdRef.current === reportId;
        // Just saved on Add: everything is already in hand — no request.
        const prefill = location.state?.prefill;
        if (prefill && prefill.report_id === reportId) {
            setReportData(prefill.data);
            setReport(toReportRow(prefill.builder_state, prefill.report_id, prefill.client_id, prefill.client_name, ""));
            setNotFound(false);
            setLoading(false);
            // Router state survives a browser refresh; drop it so a reload
            // loads the stored report instead of this one-off copy.
            navigate(location.pathname, { replace: true, state: null });
            return;
        }
        setLoading(true);
        setNetworkWarning("");
        (async () => {
            try {
                const res = await reportBuilderService.getNetworkReport({ report_id: reportId }, token);
                if (!isCurrent())
                    return;
                // A report with no connected account (404) or a payload the
                // config has moved past (422) still sends `saved_report`, so
                // the builder opens either way; only a missing report does not.
                const body = res?.data ?? res?.response?.data;
                const saved = body?.saved_report;
                if (!saved) {
                    setReport(null);
                    setNotFound(true);
                    return;
                }
                // Networks in the report the client has no account on (a clone
                // to another client) — named above the report, not toasted.
                // No account on any of them comes back as a 404 message.
                const missing = (body?.data?.unavailable_networks || []).map((entry) => entry.label);
                if (missing.length) {
                    setNetworkWarning(`In this report some of the network metrics are not included - no active account is connected for this client on: ${missing.join(", ")}.`);
                }
                else if (!body?.success && body?.message && !body?.errors) {
                    // 404 "no active account" (422 stale payload carries `errors`).
                    setNetworkWarning(body.message);
                }
                else if (!body?.success && body?.message) {
                    toast.error(body.message);
                }
                setClientAccess(saved.client_access === true);
                // The report data came back with it — the builder's first preview.
                setReportData(body?.data ?? null);
                const builderState = payloadToBuilderState(saved);
                const clientId = String(saved.client_id || "");
                setReport(toReportRow(builderState, String(saved._id || reportId), clientId, body?.data?.client?.client_name ||
                    clientOptions.find((opt) => opt.value === clientId)?.label ||
                    "", saved.created_at || ""));
                setNotFound(false);
            }
            catch {
                if (isCurrent()) {
                    setReport(null);
                    setNotFound(true);
                }
            }
            finally {
                if (isCurrent())
                    setLoading(false);
            }
        })();
        // Loaded once per report — the client list resolving later only fills
        // in a name, and must not re-run the report.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdd, reportId]);
    // On edit / view the record owns the client; on add it comes from the listing.
    const clientName = report?.client_name || selectedClientName || "-";
    // The report's own client always has an option to show, even when it is not in
    // the user's active list (an archived client, a group account).
    const headerClientOptions = useMemo(() => !isAdd && report?.client_id && !clientOptions.some((opt) => opt.value === report.client_id)
        ? [...clientOptions, { value: report.client_id, label: report.client_name || "Client" }]
        : clientOptions, [isAdd, report?.client_id, report?.client_name, clientOptions]);
    // ── Report name ─────────────────────────────────────────────────────
    /** Inline error under the name — a name this client already uses. */
    const [nameError, setNameError] = useState("");
    /**
     * Add: a new report is "Untitled Report" until it is generated. If the user
     * has not named it by then, Generate gives it "Client - Day (Campaign)" -
     * the client, the time grain and the other breakdowns in brackets. A name
     * the user typed is never touched.
     */
    const nameTouchedRef = useRef(false);
    const autoReportName = (dimensionIds) => {
        const dims = dimensionIds.map((id) => DIMENSION_LABELS[id] || id);
        const client = selectedClientName && selectedClientName !== "-" ? selectedClientName : "";
        const [first, ...rest] = dims;
        const breakdown = first ? (rest.length ? `${first} (${rest.join(", ")})` : first) : "";
        return [client, breakdown].filter(Boolean).join(" - ");
    };
    /**
     * Header rename. Add: just the local name (saved with Generate & Save).
     * Edit: PATCH the stored name; resolves to the API's message when refused,
     * which the editor shows under the field.
     */
    const handleNameChange = async (name) => {
        if (isAdd || !reportId) {
            nameTouchedRef.current = true;
            setReportName(name);
            setNameError("");
            return null;
        }
        try {
            const res = await reportBuilderService.renameReport(reportId, name, token);
            const body = res?.data ?? res?.response?.data;
            if (!body?.success) {
                return body?.message || "Could not rename the report.";
            }
            setReportName(body?.data?.report_name || name);
            setNameError("");
            toast.success(body.message || "Report renamed.");
            return null;
        }
        catch (err) {
            return err?.message || "Could not rename the report.";
        }
    };
    /**
     * Hydrated builder selection for edit / view, rebuilt from the saved
     * report_payload by `payloadToBuilderState`.
     */
    const initialBuilderState = useMemo(() => (isAdd ? null : report?.builder_state ?? null), [isAdd, report]);
    // Edit / view: adopt the record's saved range and name once it loads. Guarded
    // by a ref so a later re-fetch never overwrites what the user has since
    // changed.
    const rangeHydratedRef = useRef(false);
    useEffect(() => {
        if (!initialBuilderState || rangeHydratedRef.current)
            return;
        rangeHydratedRef.current = true;
        if (initialBuilderState.report_name)
            setReportName(initialBuilderState.report_name);
        setDateRange({
            start: isoToCalendarDate(initialBuilderState.period_start),
            end: isoToCalendarDate(initialBuilderState.period_end),
        });
        setCompareRange(initialBuilderState.compare_start && initialBuilderState.compare_end
            ? {
                start: isoToCalendarDate(initialBuilderState.compare_start),
                end: isoToCalendarDate(initialBuilderState.compare_end),
            }
            : null);
    }, [initialBuilderState]);
    // ── Save ────────────────────────────────────────────────────────────
    /**
     * Called by the builder's "Generate & Save" with the state it just
     * generated. Taking the state as an argument (rather than reading the
     * snapshot ref) avoids racing the snapshot effect — the report is known to
     * be generated by the time we get here.
     */
    const handleSave = async (builderState, payload) => {
        setShowValidation(true);
        // Still the placeholder on the first Generate: name it from the client
        // and breakdown (the header follows through the name state).
        if (isAdd &&
            !nameTouchedRef.current &&
            (!builderState.report_name.trim() || builderState.report_name.trim() === DEFAULT_REPORT_NAME)) {
            const auto = autoReportName(builderState.dimension_ids);
            if (auto) {
                builderState = { ...builderState, report_name: auto };
                setReportName(auto);
                setNameError("");
            }
        }
        if (!builderState.report_name.trim()) {
            toast.error("Give the report a name before saving.");
            return null;
        }
        if (builderState.network_keys.length === 0) {
            toast.error("Turn on at least one network before saving.");
            return null;
        }
        if (builderState.column_order.length === 0) {
            toast.error("Add at least one column before saving.");
            return null;
        }
        if (isAdd && !selectedId) {
            toast.error("Select a client before saving.");
            return null;
        }
        setSaving(true);
        try {
            // Custom metrics are stored in their own field, not inside the
            // network-report body.
            const { custom_metrics_payload, ...reportPayload } = payload;
            const clientId = report?.client_id || selectedId;
            const res = await reportBuilderService.saveReport({
                client_id: clientId,
                report_name: builderState.report_name.trim(),
                report_payload: { ...reportPayload, client_id: clientId },
                // null clears stored ones when every custom metric was removed.
                custom_metrics_payload: custom_metrics_payload ?? null,
                ...(isAdd ? { is_new: true } : {}),
            }, token);
            // A name this client already has — shown under the name field.
            const failed = res?.data ?? res?.response?.data;
            if (!failed?.success && failed?.field === "report_name") {
                setNameError(failed.message || "A report with this name already exists for this client.");
                return null;
            }
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            // 201 = a new name for this client, 200 = an existing one overwritten.
            toast.success(res.data.message || (isAdd ? "Report saved." : "Report updated."));
            // A name the client already had comes back numbered ("Name (1)") -
            // the page and the Edit page it opens carry the stored name.
            const storedName = String(res.data.report_name || "").trim();
            if (storedName && storedName !== builderState.report_name.trim()) {
                builderState = { ...builderState, report_name: storedName };
                setReportName(storedName);
                setNameError("");
            }
            // save-report ran the report too — this is the preview's data. Null
            // when no account is connected (the report is still saved).
            const data = res.data.data ?? null;
            if (!data && res.data.report_message)
                toast.error(res.data.report_message);
            // A new report continues on its Edit page, where it can be changed
            // and regenerated. `replace` so Back goes to the listing, not to a
            // blank Add form. What was just saved travels with it, so the Edit
            // page opens on it without asking the API again.
            const savedId = res.data.report_id;
            if (isAdd && savedId) {
                // The G8A AI Agent chat that built this report (a draft of the
                // client until now) becomes this report's chat, so the Edit
                // page resumes it. Best effort - never blocks or fails the save.
                await linkDraftToReport({
                    userId: String(authUser?.id ?? authUser?._id ?? ""),
                    clientId,
                    reportId: String(savedId),
                    token,
                });
                const prefill = {
                    report_id: String(savedId),
                    builder_state: builderState,
                    data,
                    client_id: clientId,
                    client_name: selectedClientName,
                };
                navigate(ROUTES.createReportEdit.path.replace(":reportId", String(savedId)), {
                    replace: true,
                    state: { prefill },
                });
            }
            return data;
        }
        catch (err) {
            toast.error(err?.message || "Could not save the report. Please try again.");
            return null;
        }
        finally {
            setSaving(false);
        }
    };
    // ── Export ──────────────────────────────────────────────────────────
    /**
     * Downloads the report on screen as an Excel file in the Divergence
     * Report's design - compare values on their own row under each row, with a
     * % CHANGE column. Built here from the generated report, so the file
     * matches the preview (custom metrics and column order included).
     */
    const handleExport = () => {
        const snapshot = snapshotRef.current;
        if (!snapshot?.report) {
            toast.info("Generate the report first, then export it.");
            return;
        }
        const { state } = snapshot;
        exportReportExcel(snapshot.report, {
            reportName: reportName || state.report_name,
            clientName: clientName !== "-" ? clientName : "",
            startDate: state.period_start,
            endDate: state.period_end,
            compareStartDate: state.compare_start,
            compareEndDate: state.compare_end,
        });
    };
    // ── Clone ───────────────────────────────────────────────────────────
    /**
     * Clone opens a dialog rather than copying straight away: the copy needs a
     * client and a name of its own, and the client cannot be picked on the page
     * itself (the header's client scope is shared with the listing).
     *
     * The SAVED report is what gets cloned (Generate saves, so it matches the
     * screen once generated); the copy then opens here on Edit.
     */
    const clone = useCloneReport();
    const handleCloneRequest = () => {
        if (!reportId)
            return;
        clone.open({
            report_id: reportId,
            report_name: reportName,
            client_id: report?.client_id || selectedId,
        });
    };
    // ── Send ────────────────────────────────────────────────────────────
    // Same as the listing's Send Report: confirm, then email the SAVED report.
    const [sendOpen, setSendOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);
    /** Notes on this report - the Notes button shows a dot when there are any.
     *  The drawer reports back after each load, so adds / deletes keep it right. */
    const [notesCount, setNotesCount] = useState(0);
    useEffect(() => {
        if (!reportId || !token || !(isEdit || isView)) {
            setNotesCount(0);
            return;
        }
        let cancelled = false;
        reportBuilderService
            .listReportNotes(reportId, token)
            .then((res) => {
            if (!cancelled)
                setNotesCount(res?.data?.success ? (res.data.data?.notes || []).length : 0);
        })
            .catch(() => {
            // No dot if the count cannot be had; the drawer reports its own errors.
        });
        return () => {
            cancelled = true;
        };
    }, [reportId, token, isEdit, isView]);
    /** Email the saved report. Shared by the dialog and the G8A AI Agent's send card. */
    const sendNow = async () => {
        if (!reportId)
            return false;
        setSending(true);
        try {
            const res = await reportBuilderService.sendReport(reportId, token);
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            toast.success(res.data.message || "Report has been sent successfully.");
            setClientAccess(true);
            return true;
        }
        catch (err) {
            toast.error(err?.message || "Could not send the report. Please try again.");
            return false;
        }
        finally {
            setSending(false);
        }
    };
    const handleSendConfirm = async () => {
        if (await sendNow())
            setSendOpen(false);
    };
    // ── Auto Schedule (G8A AI Agent) ────────────────────────────────────
    // The same PUT /reports/:id/schedule the listing's Schedule Report modal
    // makes; the server validates the config and every recipient again.
    const [scheduling, setScheduling] = useState(false);
    const scheduleNow = async (proposal) => {
        if (!reportId || !canManageAutoSchedule)
            return false;
        setScheduling(true);
        try {
            const { recipients: _recipients, replaces_existing: _replaces, ...body } = proposal;
            const res = await reportBuilderService.saveReportSchedule(reportId, body, token);
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            toast.success(res.data.message || "Report has been scheduled.");
            return true;
        }
        catch (err) {
            toast.error(err?.message || "Could not save the schedule. Please try again.");
            return false;
        }
        finally {
            setScheduling(false);
        }
    };
    /**
     * Clone and Send for the G8A AI Agent. The agent only proposes them; its
     * card's button calls these, which are the same flows the header buttons
     * and the Clone dialog run - same endpoints, same permissions, same toasts.
     */
    const aiReportActions = {
        // Chats are scoped to this report (null on Add = drafts of the client).
        reportId: !isAdd && reportId ? reportId : null,
        context: {
            saved: Boolean(reportId) && !isAdd,
            report_name: reportName,
            client_name: clientName !== "-" ? clientName : "",
            can_clone: !isAdd && !isView && Boolean(reportId),
            can_send: isEdit && canManage && Boolean(reportId),
            // Same gate as the listing's Schedule action.
            can_schedule: isEdit && canManageAutoSchedule && Boolean(reportId),
        },
        clientId: report?.client_id || selectedId,
        clientOptions,
        cloning: clone.cloning,
        sending,
        scheduling,
        onSchedule: scheduleNow,
        onCheckClone: (clientId) => clone.checkNetworksFor(reportId || "", clientId),
        onClone: (submission) => clone.cloneNow({ report_id: reportId || "", report_name: reportName, client_id: report?.client_id || selectedId }, submission),
        onSend: sendNow,
    };
    // ── Render ──────────────────────────────────────────────────────────
    const headerSubtitle = [
        clientName,
        report ? fmtDateRange(report.period_start, report.period_end) : null,
    ]
        .filter(Boolean)
        .join(" · ");
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: "8px" }, children: [_jsx(PagesCommonHeader, { leftSection: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }, children: [!isClient && (_jsx(Box, { sx: { minWidth: "200px" }, className: "client-Dropdown", children: _jsx(Dropdown
                                    // Add: the page's own pick. Edit / view: always the client
                                    // the report was made for, not whatever is picked elsewhere.
                                    , { 
                                        // Add: the page's own pick. Edit / view: always the client
                                        // the report was made for, not whatever is picked elsewhere.
                                        value: isAdd
                                            ? selectedGroupAccountId || selectedClientId
                                            : report?.client_id || "", options: headerClientOptions, onChange: handleClientChange, placeholder: "Select Client", searchable: true, disabled: !isAdd ||
                                            utilityLoading ||
                                            builderMeta.refreshing ||
                                            isRefreshing ||
                                            clientOptions?.length <= 1 }) })), _jsx(ReportNameEditor, { value: reportName, onChange: handleNameChange, disabled: isView, error: nameError })] }), rightSection: _jsx(_Fragment, { children: !isEdit && (_jsxs(Box, { sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    flexWrap: "wrap",
                                    justifyContent: "flex-end",
                                }, children: [_jsx(Box, { sx: {
                                            flexShrink: 0,
                                            "& .date-picker-trigger-container": { flexWrap: "nowrap" },
                                            "& .date-picker-trigger-field": {
                                                minHeight: "36px !important",
                                                paddingBlock: "0 !important",
                                                paddingInline: "12px !important",
                                                borderColor: "#d0d5dd !important",
                                            },
                                            "& .date-picker-trigger-field:not(.vs-separator-trigger):hover": {
                                                borderColor: "#98a2b3 !important",
                                            },
                                            "& .date-picker-trigger-field .date-display-text": {
                                                fontSize: "13px !important",
                                            },
                                        }, children: _jsx(CustomDatePicker, { value: dateRange, compareValue: compareRange, onApply: handleDateChange, comparison: true, maxPastYears: 3, disabled: isView || builderMeta.refreshing || isRefreshing, placeholder: "Select Date Range", comparePlaceholder: "Compare Range", applyLabel: builderMeta.hasReport && !isView ? "Apply and Generate" : "Apply" }) }), (isEdit || isView) && reportId && (_jsx(HoverTooltip, { title: "Report Notes", placement: "top", arrow: true, children: _jsx(IconButton, { onClick: () => setNotesOpen(true), "aria-label": "Notes", sx: {
                                                width: 36,
                                                height: 36,
                                                flexShrink: 0,
                                                borderRadius: "8px",
                                                border: "1px solid #D0D5DD",
                                                bgcolor: "#FFFFFF",
                                                color: "#344054",
                                                "&:hover": { borderColor: "#98A2B3", bgcolor: "#F9FAFB" },
                                            }, children: _jsxs("span", { className: "emailconfig-note-icon", children: [_jsx("img", { src: "/assets/note-gray.svg", alt: "notes" }), notesCount > 0 && _jsx("span", { className: "emailconfig-note-dot" })] }) }) })), isEdit && canManage && (_jsx(HoverTooltip, { title: clientAccess ? "Resend Report" : "Send Report", placement: "top", arrow: true, children: _jsx(IconButton, { onClick: () => setSendOpen(true), "aria-label": clientAccess ? "Resend Report" : "Send Report", sx: {
                                                width: 36,
                                                height: 36,
                                                flexShrink: 0,
                                                borderRadius: "8px",
                                                border: "1px solid #D0D5DD",
                                                bgcolor: "#FFFFFF",
                                                color: "#344054",
                                                "&:hover": { borderColor: "#98A2B3", bgcolor: "#F9FAFB" },
                                            }, children: _jsx(Send, { size: 18 }) }) })), (isEdit || isView) && (_jsx(HoverTooltip, { title: "Export Report", placement: "top", arrow: true, children: _jsx(IconButton, { onClick: handleExport, "aria-label": "Export Report", sx: {
                                                width: 36,
                                                height: 36,
                                                flexShrink: 0,
                                                borderRadius: "8px",
                                                border: "1px solid #D0D5DD",
                                                bgcolor: "#FFFFFF",
                                                color: "#344054",
                                                "&:hover": { borderColor: "#98A2B3", bgcolor: "#F9FAFB" },
                                            }, children: _jsx(Download, { size: 18 }) }) })), !isView && !isClient && (_jsx(AskAiButton, { onClick: () => setAskAiOpen(true), disabled: !(report?.client_id || selectedId) || builderMeta.refreshing || isRefreshing, disabledReason: "Select a client first" }))] })) }) }), isEdit && (_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: builderMeta.generatedAt, onRefresh: handleRefresh, loading: builderMeta.refreshing || isRefreshing, disabled: isRefreshing || builderMeta.refreshing, disabledReason: refreshRunning
                                ? "Data refresh is in progress. The latest data will be available once the refresh is complete."
                                : undefined, 
                            // disabledReason={
                            //     !builderMeta.hasGenerated
                            //         ? "Generate the report before refreshing"
                            //         : undefined
                            // }
                            moduleName: MODULE_KEY }), rightSection: _jsx(_Fragment, { children: isEdit && (_jsxs(Box, { sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    flexWrap: "wrap",
                                    justifyContent: "flex-end",
                                }, children: [_jsx(Box, { sx: {
                                            flexShrink: 0,
                                            "& .date-picker-trigger-container": { flexWrap: "nowrap" },
                                            "& .date-picker-trigger-field": {
                                                minHeight: "36px !important",
                                                paddingBlock: "0 !important",
                                                paddingInline: "12px !important",
                                                borderColor: "#d0d5dd !important",
                                            },
                                            "& .date-picker-trigger-field:not(.vs-separator-trigger):hover": {
                                                borderColor: "#98a2b3 !important",
                                            },
                                            "& .date-picker-trigger-field .date-display-text": {
                                                fontSize: "13px !important",
                                            },
                                        }, children: _jsx(CustomDatePicker, { value: dateRange, compareValue: compareRange, onApply: handleDateChange, comparison: true, maxPastYears: 3, disabled: isView || builderMeta.refreshing || isRefreshing, placeholder: "Select Date Range", comparePlaceholder: "Compare Range", applyLabel: builderMeta.hasReport && !isView ? "Apply and Generate" : "Apply" }) }), (isEdit || isView) && reportId && (_jsx(HoverTooltip, { title: "Report Notes", placement: "top", arrow: true, children: _jsx(IconButton, { onClick: () => setNotesOpen(true), "aria-label": "Notes", sx: {
                                                width: 36,
                                                height: 36,
                                                flexShrink: 0,
                                                borderRadius: "8px",
                                                border: "1px solid #D0D5DD",
                                                bgcolor: "#FFFFFF",
                                                color: "#344054",
                                                "&:hover": { borderColor: "#98A2B3", bgcolor: "#F9FAFB" },
                                            }, children: _jsxs("span", { className: "emailconfig-note-icon", children: [_jsx("img", { src: "/assets/note-gray.svg", alt: "notes" }), notesCount > 0 && _jsx("span", { className: "emailconfig-note-dot" })] }) }) })), isEdit && canManage && (_jsx(HoverTooltip, { title: clientAccess ? "Resend Report" : "Send Report", placement: "top", arrow: true, children: _jsx(IconButton, { onClick: () => setSendOpen(true), disabled: isRefreshing, "aria-label": clientAccess ? "Resend Report" : "Send Report", sx: {
                                                width: 36,
                                                height: 36,
                                                flexShrink: 0,
                                                borderRadius: "8px",
                                                border: "1px solid #D0D5DD",
                                                bgcolor: "#FFFFFF",
                                                color: "#344054",
                                                "&:hover": { borderColor: "#98A2B3", bgcolor: "#F9FAFB" },
                                            }, children: _jsx(Send, { size: 18 }) }) })), (isEdit || isView) && (_jsx(HoverTooltip, { title: "Export Report", placement: "top", arrow: true, children: _jsx(IconButton, { onClick: handleExport, disabled: isRefreshing, "aria-label": "Export Report", sx: {
                                                width: 36,
                                                height: 36,
                                                flexShrink: 0,
                                                borderRadius: "8px",
                                                border: "1px solid #D0D5DD",
                                                bgcolor: "#FFFFFF",
                                                color: "#344054",
                                                "&:hover": { borderColor: "#98A2B3", bgcolor: "#F9FAFB" },
                                            }, children: _jsx(Download, { size: 18 }) }) })), !isView && !isClient && (_jsx(AskAiButton, { onClick: () => setAskAiOpen(true), disabled: !(report?.client_id || selectedId) || builderMeta.refreshing || isRefreshing, disabledReason: "Select a client first" }))] })) }) })), loading ? (_jsx(Paper, { elevation: 0, sx: {
                            border: "1px solid #E4E7EC",
                            borderRadius: 2,
                            minHeight: "calc(100vh - 190px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 3,
                        }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : notFound ? (_jsx(Paper, { elevation: 0, sx: {
                            border: "1px solid #E4E7EC",
                            borderRadius: 2,
                            minHeight: "calc(100vh - 190px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 3,
                        }, children: _jsxs(Box, { sx: { textAlign: "center", maxWidth: 460 }, children: [_jsx(Typography, { sx: { fontWeight: 600, color: "#344054", mb: 0.5 }, className: "head-Font-Sizes", children: "Report not found" }), _jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#667085", mb: 2 }, children: "This report no longer exists, or the link points at a different one." }), _jsx(AppButton, { variant: "outlined", onClick: backToList, sx: {
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderColor: "#D0D5DD",
                                        color: "#344054",
                                    }, children: "Back to reports" })] }) })) : (_jsxs(_Fragment, { children: [networkWarning && (_jsx(Alert, { severity: "warning", sx: { borderRadius: "10px", alignItems: "center", mb: 2 }, children: networkWarning })), isEdit && refreshRunning && (_jsx(Alert, { severity: "info", sx: { borderRadius: "10px", alignItems: "center", mb: 2 }, children: "Data refresh is in progress. The latest data will be available once the refresh is complete." })), isEdit && refreshNotice && (_jsx(Alert, { severity: refreshNotice.severity, onClose: () => setRefreshNotice(null), sx: { borderRadius: "10px", alignItems: "center", mb: 2 }, children: refreshNotice.text })), _jsx(ReportBuilder, { mode: mode, initialState: initialBuilderState, onSnapshot: handleSnapshot, showValidation: showValidation, refreshToken: refreshToken, generateToken: generateToken, isRefreshing: isRefreshing, periodStart: calendarDateToIso(dateRange.start), periodEnd: calendarDateToIso(dateRange.end), compareStart: compareRange?.start ? calendarDateToIso(compareRange.start) : null, compareEnd: compareRange?.end ? calendarDateToIso(compareRange.end) : null, reportName: reportName, controlsOpen: controlsOpen, onSave: handleSave, saving: saving, 
                                // Nothing saved to copy yet on Add.
                                onClone: isAdd ? undefined : handleCloneRequest, cloning: clone.cloning, setControlsOpen: setControlsOpen, clientId: report?.client_id || selectedId, clientName: clientName, initialData: reportData, askAiOpen: askAiOpen, aiReportActions: aiReportActions, setAskAiOpen: setAskAiOpen, 
                                // Only fill the name in while it is still the placeholder -
                                // a name the user typed is theirs.
                                onSuggestName: (name) => {
                                    if (!reportName.trim() || reportName.trim() === DEFAULT_REPORT_NAME) {
                                        handleNameChange(name);
                                    }
                                }, 
                                // "Last month" in the chat moves the header's date picker too.
                                onSuggestPeriod: (period, compare) => {
                                    setDateRange({
                                        start: isoToCalendarDate(period.start),
                                        end: isoToCalendarDate(period.end),
                                    });
                                    setCompareRange(compare
                                        ? { start: isoToCalendarDate(compare.start), end: isoToCalendarDate(compare.end) }
                                        : null);
                                } })] }))] }), _jsx(CloneReportModal, { ...clone.modalProps, clientOptions: clientOptions, clientsLoading: utilityLoading }), _jsx(ReportActionDialog, { open: sendOpen, title: "Send Report", confirmText: "Send Report", loading: sending, onConfirm: handleSendConfirm, onClose: () => setSendOpen(false), children: _jsxs(DialogNote, { children: ["Are you sure you want to Send ", _jsx("strong", { children: reportName }), " report to the client?"] }) }), _jsx(ReportNotesDrawer, { open: notesOpen, onClose: () => setNotesOpen(false), reportId: reportId || "", reportName: reportName || "", canManage: canManageNotes, onCountChange: setNotesCount })] }));
};
export default CreateReportForm;
